// src/features/api/apiSlice.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_DOMAIN } from "../../utils/constants";
import { comp_id, create_company, create_group, delete_company, delete_group, fetch_group, generate_random, upload_file } from "./groupConstant";

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_DOMAIN,
  prepareHeaders: (headers) => {
    console.log("Fetching API: Adding headers..."); // Debug log
    const token = localStorage.getItem("authToken");

    if (!token) {
      console.warn("No auth token found! API request may fail.");
      return headers; // Return headers without Authorization
    }

    headers.set("Authorization", `Bearer ${token}`);
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

// A wrapper function to handle token verification and 401 errors
const baseQueryWithAuth = async (args, api, extraOptions) => {
  console.log("Executing baseQueryWithAuth with args:", args); // Debugging log

  const token = localStorage.getItem("authToken");

  if (!token) {
    console.warn("No auth token available. Redirecting to login...");
    window.location.href = "/login"; // Redirect if no token is present
    return { error: { status: 401, message: "Unauthorized - No Token" } };
  }

  const result = await baseQuery(args, api, extraOptions);

  console.log("API Response:", result);

  if (result?.error?.status === 401) {
    console.log("Unauthorized! Logging out...");

    // Remove token from localStorage
    localStorage.removeItem("authToken");

    // Redirect to login page
    window.location.href = "/login"; // Adjust based on routing

    // Optionally, dispatch a logout action to update Redux state
    // api.dispatch(logoutAction());
  }
  return result;
};

export const groupSlice = createApi({
  reducerPath: "groupApi", // Unique name for the slice
  baseQuery: baseQueryWithAuth,
  //   baseQuery: fetchBaseQuery({ baseUrl: BASE_DOMAIN }), // Replace with your API base URL
  endpoints: (builder) => ({
    create: builder.mutation({
      query: (credentials) => ({
        url: create_group, // Replace with your login endpoint
        method: "POST",
        body: credentials,
      }),
    }),
    fetch: builder.query({
      query: (filter) => {
        // Include the filter as query parameters
        const queryString = new URLSearchParams(filter).toString();
        return {
          url: `${fetch_group}?${queryString}`, // Append query params to the endpoint
          method: "GET",
        };
      },
    }),
    fetchcompany: builder.query({
      query: ({ url, filters, page, limit }) => {
        const queryParams = new URLSearchParams();

        // Add category array to queryParams
        if (filters.category && Array.isArray(filters.category)) {
          filters.category.forEach((item) => queryParams.append("category", item));
        }

        // Add utilityType array to queryParams
        if (filters.utilityType && Array.isArray(filters.utilityType)) {
          filters.utilityType.forEach((item) => queryParams.append("utilityType", item));
        }

        // Add other filters
        Object.keys(filters).forEach((key) => {
          if (!["category", "utilityType"].includes(key)) {
            queryParams.append(key, filters[key]);
          }
        });

        return {
          url: `${url}?${queryParams}`, // Append query parameters to the dynamic URL
          method: "GET",
        };
      },
    }),
    random: builder.query({
      query: () => {
        // Include the filter as query parameters
        return {
          url: generate_random, // Append query params to the endpoint
          method: "GET",
        };
      },
    }),
    compid: builder.query({
      query: () => {
        // Include the filter as query parameters
        return {
          url: comp_id, // Append query params to the endpoint
          method: "GET",
        };
      },
    }),
    update: builder.mutation({
      query: ({ url, data }) => ({
        url, // Use dynamic URL passed from React component
        method: "POST",
        // body: data, // Pass the data to be updated
      }),
    }),
    updategroup: builder.mutation({
      query: ({ url, formData1 }) => ({
        url, // Use dynamic URL passed from React component
        method: "POST",
        body: formData1, // Pass the data to be updated
      }),
    }),
    upload: builder.mutation({
      query: (credentials) => ({
        url: upload_file, // Replace with your login endpoint
        method: "POST",
        body: credentials,
      }),
    }),
    createcompany: builder.mutation({
      query: (credentials) => ({
        url: create_company, // Replace with your login endpoint
        method: "POST",
        body: credentials,
      }),
    }),
    deleteCompany: builder.mutation({
      query: (id) => ({
        url: `${delete_company}/${id}`, // Append the company ID to the endpoint
        method: "DELETE", // Use the DELETE method
      }),
    }),
    deleteGroup: builder.mutation({
      query: (id) => ({
        url: `${delete_group}/${id}`, // Append the company ID to the endpoint
        method: "DELETE", // Use the DELETE method
      }),
    }),
    toggleCompany: builder.mutation({
      query: (id) => ({
        url: `${delete_company}/${id}/toggle-status`, // Append the company ID to the endpoint
        method: "POST", // Use the DELETE method
      }),
    }),
    updateCompany: builder.mutation({
      query: ({ id, body }) => ({
        url: `${delete_company}/${id}`, // Append the company ID to the endpoint
        method: "POST", // Use the DELETE method
        body,
      }),
    }),
    updateEmail: builder.mutation({
      query: ({ id, body }) => ({
        url: `${BASE_DOMAIN}companies/updateEmailDetails/${id}`,
        method: "PUT",
        body,
      }),
    }),
  }),
});

export const {
  useCreateMutation,
  useFetchQuery,
  useRandomQuery,
  useCompidQuery,
  useUpdateMutation,
  useUploadMutation,
  useUpdategroupMutation,
  useCreatecompanyMutation,
  useFetchcompanyQuery,
  useDeleteCompanyMutation,
  useDeleteGroupMutation,
  useToggleCompanyMutation,
  useUpdateCompanyMutation,
  useUpdateEmailMutation,
} = groupSlice; // Export the auto-generated hook
